import { FC, useState } from "react";
import { useDetailBookingStore } from "../detail-booking.store";
import { Plus, Pin } from "lucide-react";
import { getClientName, formatMentions } from "~/utils";
import { NOTE_KEYS, useCreateNote, useGetNotes } from "~/services/note";
import { useNotification } from "~/hooks";
import { SortOrder, Role } from "~/utils/constants";
import { Spin } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import { MentionTextArea } from "~/components/common/mentions/MentionTextArea";
import { useUsers } from "~/services/user/user.hooks";

type Props = {};

export const Notes: FC<Props> = () => {
  const qc = useQueryClient();
  const { booking } = useDetailBookingStore();
  const [content, setContent] = useState("");
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const [isPinned, setIsPinned] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const { success, error } = useNotification();

  const { mutate, isPending } = useCreateNote();

  const { data: notesData, isLoading } = useGetNotes({
    jobTicketId: booking?.id,
    sortByCreatedAt: SortOrder.DESC,
  });

  // Fetch all users for mentions (admin and staff only)
  const { data: usersData } = useUsers({
    roles: [Role.ADMIN, Role.SUPER_ADMIN, Role.STAFF, Role.LEAD],
    pageSize: 100, // Fetch all available users (backend max is 100)
  });

  if (!booking) {
    return <div className="text-gray-500">No booking data available</div>;
  }

  const handleAddNote = () => {
    if (!content.trim()) return;

    mutate(
      {
        content,
        jobTicketId: booking.id,
        isPinned,
        mentionedUserIds: mentionedUserIds.length > 0 ? mentionedUserIds : undefined,
      },
      {
        onSuccess: () => {
          qc.invalidateQueries({
            queryKey: NOTE_KEYS.GET_NOTES({}),
          });
          success({
            message: "Note added successfully",
          });
          // Reset form
          setContent("");
          setMentionedUserIds([]);
          setIsPinned(false);
          setShowForm(false);
        },
        onError: (err) => {
          error({
            message: "Failed to add note",
            description: err.message,
          });
        },
      }
    );
  };

  const toggleForm = () => {
    setShowForm(!showForm);
    if (!showForm) {
      setContent("");
      setMentionedUserIds([]);
      setIsPinned(false);
    }
  };

  const availableUsers = usersData?.items || [];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium">Notes</h3>
        <button
          className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-1"
          onClick={toggleForm}
        >
          <Plus size={16} />
          <span>Add Note</span>
        </button>
      </div>

      {showForm && (
        <div className="bg-white border rounded-lg p-4 mb-6">
          <h4 className="font-medium mb-3">Add New Note</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note
              </label>
              <MentionTextArea
                value={content}
                onChange={(newContent, newMentionedUserIds) => {
                  setContent(newContent);
                  setMentionedUserIds(newMentionedUserIds);
                }}
                users={availableUsers}
                placeholder="Type your note here. Use the dropdown below to @mention team members..."
                disabled={isPending}
                rows={4}
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="pin-note"
                className="h-4 w-4 text-blue-600 rounded"
                checked={isPinned}
                onChange={(e) => setIsPinned(e.target.checked)}
                disabled={isPending}
              />
              <label htmlFor="pin-note" className="text-sm">
                Pin this note
              </label>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                className="px-3 py-2 border rounded-lg hover:bg-gray-50"
                onClick={toggleForm}
                disabled={isPending}
              >
                Cancel
              </button>
              <button
                className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
                onClick={handleAddNote}
                disabled={isPending || !content.trim()}
              >
                {isPending ? "Saving..." : "Save Note"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Spin spinning={isLoading}>
        <div className="border rounded-lg overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h4 className="font-medium">Note History</h4>
          </div>

          <div className="divide-y">
            {notesData?.items.map((note) => (
              <div
                key={note.id}
                className={`p-4 hover:bg-gray-50 ${
                  note.isPinned ? "bg-blue-50" : ""
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="font-medium flex items-center">
                    {note.isPinned && (
                      <Pin size={16} className="mr-1 text-blue-600" />
                    )}
                    {getClientName(note.author)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(note.createdAt).toLocaleString()}
                  </div>
                </div>
                <div className="mt-2 whitespace-pre-wrap">{formatMentions(note.content)}</div>
              </div>
            ))}
          </div>
        </div>
      </Spin>
    </div>
  );
};
